import { useCallback, useRef, useState } from 'react';
import { FileError, FileRejection } from 'react-dropzone';

import { FileImage, MAX_SIZE_OF_FILE } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFileUploadQuery from '@/modules/transaction/hooks/useFileUploadQuery';

import validateFiles from './utils/validateFiles';
import useFilesInDocumentStore from './useFilesInDocument.store';

export interface UseOnDropProps {
  onFilesAdded?: (Files: FileImage[], currentDocumentId?: string, isError?: boolean) => void,
  onFilesSelect?: () => void,
  onFileUploadError?: (error?: FileError) => void,
  disableFilesUpload?: boolean;
  storeKey?: string;
  ocrRequired?: boolean;
}

const useOnDrop = ({
  onFilesAdded,
  onFilesSelect,
  onFileUploadError,
  disableFilesUpload,
  storeKey,
  ocrRequired = false,
}: UseOnDropProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { values: { documentId, files }, handleSetValues } = useFilesInDocumentStore(storeKey);
  const { createDocumentId, uploadFiles } = useFileUploadQuery();
  const isFilesUploadError = useRef<boolean>(null);

  const handleFileUploadError = useCallback((error?: FileError) => {
    isFilesUploadError.current = true;
    onFileUploadError?.(error);
  }, [onFileUploadError]);

  const onDrop = async <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
  ) => {
    isFilesUploadError.current = false;

    if (onFilesSelect) {
      onFilesSelect();
    }

    const isFileValidationError = await validateFiles({
      acceptedFiles,
      fileRejections,
      onFileUploadError: handleFileUploadError,
    });

    if (isFileValidationError) {
      return;
    }
  
    const allFiles = [...acceptedFiles, ...fileRejections
      ? fileRejections
        .map((item) => item.file)
        .filter((item) => item.size <= MAX_SIZE_OF_FILE) : []];

    if (!allFiles.length) return;

    if (disableFilesUpload) {
      const preparedFiles = allFiles.map(prepareFile);
      onFilesAdded?.([...preparedFiles as unknown as FileImage[]] as FileImage[]);
      return;
    }

    setIsLoading(true);
    let createdDocumentId = '';
    if (!documentId) {
      createdDocumentId = await createDocumentId();
      handleSetValues({ documentId: createdDocumentId });
    }
    const preparedFiles = allFiles.map(prepareFile);
    const currentDocumentId = documentId || createdDocumentId;
    const uploadedFiles = await uploadFiles(
      currentDocumentId,
      preparedFiles as FileImage[],
      ocrRequired,
      handleFileUploadError
    );
    setIsLoading(false);
    handleSetValues({ files: [...files, ...uploadedFiles.filter((file) => file.id)] });

    onFilesAdded?.([
      ...uploadedFiles as unknown as FileImage[],
    ] as FileImage[], currentDocumentId, isFilesUploadError.current);
  };

  return {
    onDrop,
    isLoading,
  };
};

export default useOnDrop;
